/**
 * Reveals editorial content as it scrolls into view.
 *
 * A single shared element rather than one observer per block: the spec table,
 * weight scale, process steps and timeline all want the same behaviour, and the
 * animation itself lives in each block's own stylesheet keyed off the
 * `is-revealed` attribute. This only decides when.
 *
 * Deliberately not a Component subclass — there is no state, no refs and
 * nothing to re-render, so the base class would only add weight.
 */

/** Shared across every instance; one observer is cheaper than dozens. */
let observer;

function getObserver() {
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.setAttribute('is-revealed', '');
        // Reveal is one-way. Re-animating on the way back up is distracting
        // and makes long pages feel unstable.
        observer?.unobserve(entry.target);
      }
    },
    // Fires a little before the element's top edge reaches the viewport floor,
    // so content is already settled by the time it is properly in view.
    { rootMargin: '0px 0px -12% 0px', threshold: 0 }
  );

  return observer;
}

class EditorialReveal extends HTMLElement {
  connectedCallback() {
    // Honour the OS setting, and degrade to "already revealed" wherever
    // IntersectionObserver is missing, so content is never stranded hidden.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      this.setAttribute('is-revealed', '');
      return;
    }

    getObserver().observe(this);
  }

  disconnectedCallback() {
    observer?.unobserve(this);
  }
}

if (!customElements.get('editorial-reveal')) {
  customElements.define('editorial-reveal', EditorialReveal);
}
